/**
 * Custom hook for processing challenge data for farm details.
 * Extracted from FarmDetailsModal.tsx.
 */

import { useMemo, useEffect, useState, useRef } from "react";
import { formService, FormDto } from "@/api/services/form.service";
import { answerService, AnswerDto } from "@/api/services/answer.service";
import { formDtoToForm } from "@/api/services/form.service";

interface UseChallengeDataProps {
  challengesTableData: {
    data?: {
      challenges: any[];
      programVersionItems: any[];
    };
  };
  organizationId: string;
  ownOrgId?: string;
}

interface ChallengeDataResult {
  allItemsByLevel: { [levelName: string]: any[] };
  farmItemsMap: { [pviId: string]: any };
  sortedLevels: [string, any[]][];
  approvedItemsByLevel: { [levelName: string]: any[] };
  sortedApprovedLevels: [string, any[]][];
  formsMap: Record<string, any>; // Map of formId to Form object
  answersMap: Record<string, AnswerDto[]>; // Map of itemId to AnswerDto[]
  isLoadingForms: boolean;
}

/**
 * Hook for processing challenge data
 */
export function useChallengeData({
  challengesTableData,
  organizationId,
  ownOrgId,
}: UseChallengeDataProps): ChallengeDataResult {
  const [formsMap, setFormsMap] = useState<Record<string, any>>({});
  const [answersMap, setAnswersMap] = useState<Record<string, AnswerDto[]>>({});
  const [isLoadingForms, setIsLoadingForms] = useState(false);

  // Track which organization we've already loaded data for to prevent duplicate calls
  const loadedOrgRef = useRef<string | null>(null);
  const loadedDataHashRef = useRef<string | null>(null);

  // Process and structure all challenge data
  const { allItemsByLevel, farmItemsMap } = useMemo(() => {
    const result: {
      allItemsByLevel: { [levelName: string]: any[] };
      farmItemsMap: { [pviId: string]: any };
    } = { allItemsByLevel: {}, farmItemsMap: {} };

    if (!challengesTableData?.data) return result;

    const { challenges, programVersionItems } = challengesTableData.data;

    // 1. Group all possible program items by level and sort them
    programVersionItems.forEach((pvi: any) => {
      const levelName = pvi.programLevelName || "General";
      if (!result.allItemsByLevel[levelName]) {
        result.allItemsByLevel[levelName] = [];
      }
      result.allItemsByLevel[levelName].push(pvi);
    });

    for (const levelName in result.allItemsByLevel) {
      result.allItemsByLevel[levelName].sort((a, b) => {
        const cpA = a.cpNumber || "";
        const cpB = b.cpNumber || "";
        return cpA.localeCompare(cpB, undefined, { numeric: true });
      });
    }

    // 2. Create a lookup map for the specific farm's inspection items
    const challenge = challenges.find(
      (c: any) => c.inspectedOrgId === organizationId,
    );
    if (challenge) {
      challenge.inspectionsProgramsVersionsItems.forEach((item: any) => {
        result.farmItemsMap[item.programVersionItemId] = item;
      });
    }

    return result;
  }, [challengesTableData, organizationId]);

  // Create a sorted list of the levels for consistent rendering
  const sortedLevels = useMemo(() => {
    return Object.entries(allItemsByLevel).sort(([levelA], [levelB]) =>
      levelA.localeCompare(levelB),
    );
  }, [allItemsByLevel]);

  const approvedItemsByLevel = useMemo(() => {
    const groups: { [levelName: string]: any[] } = {};
    for (const levelName in allItemsByLevel) {
      const approvedItems = allItemsByLevel[levelName]
        .map((pvi) => {
          const farmItem = farmItemsMap[pvi.id];
          // Include item if it exists, regardless of approval status
          return farmItem ? { ...farmItem, pvi } : null;
        })
        .filter((item) => item !== null); // Filter out nulls

      if (approvedItems.length > 0) {
        groups[levelName] = approvedItems as any[];
      }
    }
    return groups;
  }, [allItemsByLevel, farmItemsMap]);

  // Create a sorted list of the approved levels for consistent rendering
  const sortedApprovedLevels = useMemo(() => {
    return Object.entries(approvedItemsByLevel).sort(([levelA], [levelB]) =>
      levelA.localeCompare(levelB),
    );
  }, [approvedItemsByLevel]);

  // Batch preload forms and answers using the new API
  useEffect(() => {
    if (!challengesTableData?.data || !ownOrgId) {
      setFormsMap({});
      setAnswersMap({});
      loadedOrgRef.current = null;
      loadedDataHashRef.current = null;
      return;
    }

    const { programVersionItems, challenges } = challengesTableData.data;

    // Collect all unique formIds from program version items
    const formIds = new Set<string>();
    const itemIds = new Set<string>();

    programVersionItems.forEach((pvi: any) => {
      if (pvi.formId) {
        formIds.add(pvi.formId);
      }
    });

    // Collect all itemIds from the farm's inspection items
    const challenge = challenges.find(
      (c: any) => c.inspectedOrgId === organizationId,
    );
    if (challenge) {
      challenge.inspectionsProgramsVersionsItems.forEach((item: any) => {
        itemIds.add(item.id);
      });
    }

    if (formIds.size === 0 && itemIds.size === 0) {
      setFormsMap({});
      setAnswersMap({});
      loadedOrgRef.current = null;
      loadedDataHashRef.current = null;
      return;
    }

    // Create a hash of the data we're about to load to detect actual changes
    const formIdsArray = Array.from(formIds).sort();
    const itemIdsArray = Array.from(itemIds).sort();
    const dataHash = `${organizationId}:${formIdsArray.join(',')}:${itemIdsArray.join(',')}`;

    // Skip if we've already loaded this exact data
    if (loadedDataHashRef.current === dataHash) {
      return;
    }

    loadedOrgRef.current = organizationId;
    loadedDataHashRef.current = dataHash;

    setIsLoadingForms(true);

    // Batch load forms and answers in parallel
    Promise.all([
      formIdsArray.length > 0 ? formService.getByIds(ownOrgId, formIdsArray) : Promise.resolve({ data: {} }),
      itemIdsArray.length > 0 ? answerService.getByItemIds(ownOrgId, itemIdsArray) : Promise.resolve({ data: {} }),
    ])
      .then(([formsResponse, answersResponse]: [any, any]) => {
        // Convert FormDtos to Form objects for FormAnswersViewer
        const forms: Record<string, any> = {};
        if (formsResponse.data) {
          Object.entries(formsResponse.data).forEach(([formId, formDto]: [string, any]) => {
            forms[formId] = formDtoToForm(formDto as FormDto);
          });
        }
        setFormsMap(forms);
        setAnswersMap(answersResponse.data || {});
      })
      .catch((error) => {
        console.error('Error loading forms/answers:', error);
        setFormsMap({});
        setAnswersMap({});
      })
      .finally(() => {
        setIsLoadingForms(false);
      });
  }, [challengesTableData?.data, ownOrgId, organizationId]);

  return {
    allItemsByLevel,
    farmItemsMap,
    sortedLevels,
    approvedItemsByLevel,
    sortedApprovedLevels,
    formsMap,
    answersMap,
    isLoadingForms,
  };
}
